import { useState } from 'react';
import { api } from '../lib/api';
import { AppleOfferRedeem } from '../lib/appleOfferRedeem';
import { rememberRefCode } from '../lib/influencerTracking';
import { restoreApplePurchases } from '../lib/appleIap';
import { checkoutErrText } from '../lib/subscriptionCheckout';
import { btnGhost, btnPrimary } from '../lib/uiClasses';
import type { AppleIapPlan } from '../lib/appleIapProducts';

type Props = {
  plan: AppleIapPlan;
  disabled?: boolean;
  /** Called when a code is applied so parent can show discounted price. */
  onApplied?: (info: { code: string; firstMonthPriceLabel: string }) => void;
  onUnlocked?: () => void;
};

function firstMonthLabel(plan: AppleIapPlan) {
  return plan === 'adfree' ? '$1.50' : '$3.99';
}

/**
 * Stripe-like promo field: Apply updates our UI price + attribution, then
 * Continue opens Apple's in-app redeem sheet (required for the real discount).
 */
export function AppleOfferCodeField({ plan, disabled, onApplied, onUnlocked }: Props) {
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const promoPrice = firstMonthLabel(plan);

  async function applyCode() {
    setErr(null);
    setMsg(null);
    const normalized = code
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9_-]/g, '');
    if (normalized.length < 2) {
      setErr('Enter a promo code.');
      return;
    }
    setBusy(true);
    try {
      rememberRefCode(normalized);
      try {
        await api('/api/auth/apply-influencer-code', {
          method: 'POST',
          body: JSON.stringify({ code: normalized }),
        });
      } catch {
        // Still allow Apple redeem for ASC-only codes.
      }
      setApplied(normalized);
      onApplied?.({ code: normalized, firstMonthPriceLabel: promoPrice });
      setMsg(`Code applied — first month ${promoPrice}. Tap Continue to confirm with Apple.`);
    } catch (e) {
      setErr(checkoutErrText(e));
    } finally {
      setBusy(false);
    }
  }

  async function continueRedeem() {
    setErr(null);
    setMsg(null);
    setBusy(true);
    try {
      if (!applied) await applyCode();
      await AppleOfferRedeem.presentRedeemSheet();
      setMsg('If Apple accepted the code, tap Done below to unlock your account.');
      // Give StoreKit a moment, then try restore/verify.
      await new Promise((r) => setTimeout(r, 800));
      const restored = await restoreApplePurchases();
      if (restored) {
        setMsg(`Unlocked at ${promoPrice} first month.`);
        onUnlocked?.();
      }
    } catch (e) {
      setErr(checkoutErrText(e));
    } finally {
      setBusy(false);
    }
  }

  async function finishAfterRedeem() {
    setErr(null);
    setBusy(true);
    try {
      const restored = await restoreApplePurchases();
      if (!restored) {
        setErr('No discounted subscription found yet. Finish Apple’s sheet, then try again.');
        return;
      }
      setMsg('Subscription unlocked.');
      onUnlocked?.();
    } catch (e) {
      setErr(checkoutErrText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 rounded-2xl border border-sky-400/20 bg-sky-500/5 px-3 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-200/80">Promo code</p>
      <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">
        Enter your code to set first month to <strong className="text-zinc-300">{promoPrice}</strong> (then
        regular price). Apple confirms the discount on the next sheet.
      </p>
      <div className="mt-2 flex gap-2">
        <input
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            setApplied(null);
          }}
          placeholder="ENTER CODE"
          autoCapitalize="characters"
          autoCorrect="off"
          disabled={disabled || busy}
          className="min-w-0 flex-1 rounded-xl border border-white/15 bg-black/30 px-3 py-2.5 font-mono text-sm text-zinc-100 outline-none focus:border-sky-500/40"
        />
        <button
          type="button"
          disabled={disabled || busy || code.trim().length < 2}
          onClick={() => void applyCode()}
          className={`${btnGhost} shrink-0 px-3 text-sm disabled:opacity-50`}
        >
          Apply
        </button>
      </div>

      {applied ? (
        <div className="mt-3 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-center">
          <p className="text-[11px] uppercase tracking-wide text-emerald-200/80">First month</p>
          <p className="font-display text-2xl font-bold text-white">{promoPrice}</p>
          <p className="text-[11px] text-zinc-500">Code {applied}</p>
        </div>
      ) : null}

      {applied ? (
        <div className="mt-3 flex flex-col gap-2">
          <button
            type="button"
            disabled={disabled || busy}
            onClick={() => void continueRedeem()}
            className={`${btnPrimary} w-full text-sm`}
          >
            {busy ? 'Opening Apple…' : `Continue · unlock ${promoPrice}`}
          </button>
          <button
            type="button"
            disabled={disabled || busy}
            onClick={() => void finishAfterRedeem()}
            className={`${btnGhost} w-full text-xs`}
          >
            Done redeeming — unlock account
          </button>
        </div>
      ) : null}

      {msg ? <p className="mt-2 text-xs text-emerald-300/90">{msg}</p> : null}
      {err ? <p className="mt-2 text-xs text-rose-300">{err}</p> : null}
    </div>
  );
}
